import type { ButtonStyle, ButtonVariant } from '@liveauctioneers/hammer-ui-core/types';

import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Bell, Check } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { toast } from '@liveauctioneers/hammer-ui-core/toast';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useCallback, useEffect, type ReactNode } from 'react';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useRequireLogin } from '@/components/ShouldLogIn/hooks/useRequireLogin';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { CompleteAccountModalContext } from '@/redux/modules/modal';
import {
    fetchSimilarItemAlert,
    removeSimilarItemAlert,
    submitSimilarItemAlert,
} from '@/redux/modules/similarItemAlerts/similarItemAlerts.actions';
import {
    getIsSimilarItemAlertSet,
    getSimilarItemAlertId,
} from '@/redux/modules/similarItemAlerts/similarItemAlerts.selectors';
import {
    SimilarItemAlertSurface,
    useTrackSimilarItemAlertRemoved,
    useTrackSimilarItemAlertSet,
} from '@/utils/amplitude/analytics/similarItems/trackSimilarItemAlert.analytics';
import { useSimilarItemAlertsExperiment } from '@/utils/amplitude/experiments/hooks/useSimilarItemAlerts.experiment';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { getForYouUrl } from '@/utils/urls';

type Props = {
    className?: string;
    /**
     * Rendered in place of the CTA for the experiment's control arm. The PDP has nothing to fall
     * back to (control shows no widget), so it stays undefined there; Price Results passes the
     * "Follow This Search" button it replaces.
     */
    fallback?: ReactNode;
    itemId: number;
    style?: ButtonStyle;
    surface?: SimilarItemAlertSurface;
    variant?: ButtonVariant;
};

/**
 * The Similar Item Alert toggle. It owns the experiment lookup for its surface and is the single
 * always-mounted caller of the hook on the page, so exposure fires once for BOTH arms — control
 * renders `fallback` (or nothing), treatment renders the CTA. Mounting a second caller in a parent
 * would double-fire exposure for treatment only and skew the experiment.
 *
 * On the ended-lot PDP it goes into hammer-ui's `BidCanvasVariations.similarItemAlertComponent`
 * slot, which only mounts it where "Sell a Similar Item" shows (i.e. an ended lot); on Price
 * Results it replaces the "Follow This Search" button. Both spots render for every qualifying
 * bidder, which is what scopes exposure to the people who could actually see the CTA.
 */
export const SimilarItemAlertCTA = ({
    className = 'mt-xs w-full',
    fallback,
    itemId,
    style = 'primary',
    surface = SimilarItemAlertSurface.PDP,
    variant = 'transparent',
}: Props) => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const verifyLogin = useRequireLogin();
    const hasSimilarItemAlerts = useSimilarItemAlertsExperiment();

    const userIsLoggedIn = useAppSelector(isUserLoggedIn);
    const isAlertSet = useAppSelector((state) => getIsSimilarItemAlertSet(state, itemId));
    const savedItemId = useAppSelector((state) => getSimilarItemAlertId(state, itemId));
    const lotTitle = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const trackAlertSet = useTrackSimilarItemAlertSet();
    const trackAlertRemoved = useTrackSimilarItemAlertRemoved();

    // The check endpoint is JWT-only, so a logged-out bidder just sees the OFF state until they
    // sign in. Control never needs the lookup — it renders nothing.
    useEffect(() => {
        if (hasSimilarItemAlerts && userIsLoggedIn) {
            dispatch(fetchSimilarItemAlert({ lotId: itemId }));
        }
    }, [dispatch, hasSimilarItemAlerts, itemId, userIsLoggedIn]);

    // These read the dispatched action rather than unwrapping it: a rejected thunk would throw out
    // of the click handler as an unhandled rejection. A failed request leaves the CTA on its
    // current state and shows no toast, which is the honest thing to show until the feature has a
    // designed error state.
    const setAlert = useCallback(async () => {
        const action = await dispatch(submitSimilarItemAlert({ lotId: itemId, lotTitle }));
        if (!submitSimilarItemAlert.fulfilled.match(action)) {
            return false;
        }
        trackAlertSet({ itemId, surface });
        return true;
    }, [dispatch, itemId, lotTitle, surface, trackAlertSet]);

    const handleSetAlert = useCallback(async () => {
        if (!(await setAlert())) {
            return;
        }
        toast('success', {
            description: (
                <>
                    {formatMessage(['similarItemAlerts', 'alertCreatedDescription'])}{' '}
                    <RouteLink to={getForYouUrl()}>{formatMessage(['similarItemAlerts', 'manageAlerts'])}</RouteLink>
                </>
            ),
            title: String(formatMessage(['similarItemAlerts', 'alertCreatedTitle'])),
        });
    }, [formatMessage, setAlert]);

    const handleRemoveAlert = useCallback(async () => {
        const action = await dispatch(removeSimilarItemAlert({ lotId: itemId, savedItemId }));
        if (!removeSimilarItemAlert.fulfilled.match(action)) {
            return;
        }
        trackAlertRemoved({ itemId, surface });
        toast('success', {
            // Undo re-creates the alert, so it comes back with a new savedItemId. That's expected —
            // the row is gone, this is not a restore.
            description: <InlineButton onClick={setAlert}>{formatMessage(['similarItemAlerts', 'undo'])}</InlineButton>,
            title: String(formatMessage(['similarItemAlerts', 'alertRemovedTitle'])),
        });
    }, [dispatch, formatMessage, itemId, savedItemId, setAlert, surface, trackAlertRemoved]);

    const handleClick = useCallback(async () => {
        await verifyLogin({
            modalContext: { id: itemId, type: CompleteAccountModalContext.SimilarItemAlert },
            requireLogin: true,
            runFunction: isAlertSet ? handleRemoveAlert : handleSetAlert,
        });
    }, [handleRemoveAlert, handleSetAlert, isAlertSet, itemId, verifyLogin]);

    if (!hasSimilarItemAlerts) {
        return fallback;
    }

    return (
        <Button
            // Button is built for short labels: it forces `nowrap` and a single-line `leading`, so
            // below ~420px this copy overflows its own padding and the icon lands outside the
            // button. Allow wrapping on mobile rather than shortening the approved copy — the
            // label still sits on one line wherever it fits, so only the narrow case changes.
            className={cn(className, 'mobile:h-auto mobile:whitespace-normal mobile:leading-normal')}
            iconLeft={isAlertSet ? Check : Bell}
            onClick={handleClick}
            style={style}
            variant={variant}
        >
            {isAlertSet
                ? formatMessage(['similarItemAlerts', 'alertsAreOn'])
                : formatMessage(['similarItemAlerts', 'notifyMe'])}
        </Button>
    );
};
